import { api } from './client'

/** Timepoint vocabulary for longitudinal re-test (PRD §8.1). Mirrors the
 *  backend's TIMEPOINT_VALUES. */
export type Timepoint = 'unica' | 'T1' | 'T2' | 'T3' | 'T4'

export const TIMEPOINT_VALUES: Timepoint[] = ['unica', 'T1', 'T2', 'T3', 'T4']

export interface SubscaleScore {
  raw: number
  min: number
  max: number
  label: string
}

export interface ScoresSnapshot {
  subscales: Record<string, SubscaleScore>
  total: SubscaleScore | null
}

export interface ResponseSummary {
  id: number
  code: string
  /** Nombre en castellano del cuestionario, tal cual lo manda el backend
   *  (`known_questionnaires`), para no mantener un mapa code→nombre en la UI. */
  name_es?: string
  timepoint: string
  submitted_at: string
  /** Sesión a la que pertenece la respuesta. Sólo PRE-SESION la trae: el
   *  chequeo previo se responde una vez POR SESIÓN dentro del vr-app. */
  session_id?: string | null
  /** `started_at` de esa sesión — la fecha que se muestra en la lista, más
   *  fiel que `submitted_at` para un chequeo hecho justo antes de jugar. */
  session_started_at?: string | null
}

/** Chequeo previo: cuestionario por sesión, obligatorio, respondido DENTRO
 *  del flujo de tablet. No se responde ni se edita desde
 *  la web — acá sólo se lista lo ya contestado. */
export const PRE_SESSION_CODE = 'PRE-SESION'

/** Valor persistido por ítem. Likert / number → number; single_select /
 *  text → string. Los items opcionales no respondidos no aparecen en el
 *  diccionario. */
export type AnswerValue = number | string | string[]

/**
 * El jugador nunca ve `scores` en sus propios endpoints (`/know/*`) — la
 * devolución psicométrica la da el equipo desde el panel admin, como acto
 * humano separado (ver docs/30-club-onboarding-and-reporting-prd.md). Por la
 * misma razón, `answers` sólo viaja para cuestionarios `editable` (hoy
 * ninguno alcanzable del catálogo activo) — permitir las respuestas crudas
 * de un instrumento con clave de corrección pública (SMTQ) sería la misma
 * fuga que mandar `scores` directamente. Queda `?` porque el backend omite
 * la key entera cuando no aplica.
 */
export interface ResponseDetail extends ResponseSummary {
  answers?: Record<string, AnswerValue>
}

export interface LikertOption {
  value: number
  label: string
}

export interface ChoiceOption {
  value: string
  label: string
}

export type ItemKind =
  | 'likert'
  | 'single_select'
  | 'number'
  | 'text'
  | 'slider'
  | 'date'
  | 'multi_select'

export interface ItemDef {
  id: number
  /** Clave estable del ítem cuando la definición la declara (hoy: los tres
   *  del chequeo previo, que son las columnas de covariables de la sesión).
   *  `null` en los instrumentos validados, que se indexan por `id`. */
  key: string | null
  text: string
  subscale: string
  reverse: boolean
  kind: ItemKind
  required: boolean
  help_es: string | null
  options: ChoiceOption[]
  min_value: number | null
  max_value: number | null
  unit: string | null
  step: number | null
  min_label: string | null
  max_label: string | null
  max_length: number
}

export interface SubscaleDef {
  key: string
  label: string
  items: number[]
  min: number
  max: number
  description: string
}

export interface QuestionnaireDefinition {
  code: string
  name: string
  name_es: string
  instructions_es: string
  administration_es: string | null
  notes_es: string | null
  editable: boolean
  required_for_vr: boolean
  likert: LikertOption[]
  items: ItemDef[]
  subscales: SubscaleDef[]
  total: { label: string; min: number; max: number } | null
}

export interface ProfileDto {
  user_id: number
  username: string
  email: string | null
  display_name: string | null
  handedness: 'right' | 'left' | 'both' | null
  dominant_foot: 'right' | 'left' | 'both' | null
  height_cm: number | null
  // Demographic block (live aquí ahora — antes era el cuestionario
  // DEMOGRAPHIC). Vocabularios cerrados en /know/me/profile/options.
  age: number | null
  gender: string | null
  position: string | null
  vr_experience: string | null
  vision_notes: string | null
  /** Campos del REQUIRED_FOR_VR todavía vacíos (subset de las keys del DTO). */
  pending_profile_fields: string[]
  /** Lista canónica de campos que el onboarding requiere completar. */
  required_for_vr_fields: string[]
}

/** Body de `PATCH /know/me/profile`: parcial, sólo los campos presentes se
 *  tocan. Lo arma `pages/data/profilePatch.ts`. */
export type ProfilePatch = Partial<
  Omit<ProfileDto, 'user_id' | 'username' | 'pending_profile_fields' | 'required_for_vr_fields'>
>

export interface ProfileOptions {
  gender: string[]
  position: string[]
  vr_experience: string[]
  age: { min: number; max: number }
  vision_notes_max_length: number
  required_for_vr: string[]
}

/** Shape de `POST /know/me/consent` — mismo shape que devuelve el admin en
 *  `/admin/users/{id}/consents` (ver backend `_consent_dict`). */
export interface ConsentDto {
  id: number
  user_id: number
  consent_version: string
  signed_at: string
  is_parental: boolean
  recorded_by_user_id: number | null
  revoked_at: string | null
  notes: string | null
  created_at: string
}

/** Club que firma una invitación (branding del landing público). Mismo
 *  subset que devuelve `/staff-invites/{token}`. */
export interface InvitationClub {
  id: number
  name: string
  logo_url: string | null
  primary_color: string | null
}

export interface InvitationInfo {
  status: 'pending' | 'scheduled' | 'used' | 'expired' | 'revoked' | 'unknown'
  email: string
  questionnaire_code: string
  questionnaire_name_es: string
  /** `null` para invitaciones globales (superadmin) o sin club atribuible. */
  club: InvitationClub | null
  expires_at: string
}

export interface PerformanceSummary {
  total_sessions: number
  closed_sessions: number
  total_trials: number
  goals: number
  saves: number
  misses: number
  goal_rate: number | null
  average_score: number | null
  best_score: number | null
  last_session_at: string | null
}

export interface PerformanceSession {
  id: string
  started_at: string | null
  ended_at: string | null
  status: string
  n_trials: number
  goals: number
  saves: number
  misses: number
  score: number
  sync_quality?: string | null
}

export interface PerformanceDto {
  summary: PerformanceSummary
  outcomes: Record<string, number>
  recent_sessions: PerformanceSession[]
}

/** Ranking global — mismo endpoint que pinta el lobby del vr-app
 *  (`vr-app/src/api/ranking.ts`). El scenario es `penalty` en Fase 1. */
export type RankingPeriod = 'all_time' | 'weekly' | 'monthly'

export interface RankingEntry {
  rank: number
  user_id: number
  /** Nombre visible ya saneado por el backend: nunca el username/email
   *  (revision de privacidad 2026-09-07, H-1). */
  display_name: string
  score: number
  n_trials: number
  n_sessions: number
}

/** Puesto del jugador logueado dentro del ranking COMPLETO (no de la página
 *  `limit`), para "Tu puesto: #k de N". `null` cuando todavía no rankeó. */
export interface RankingMe {
  rank: number
  user_id: number
  score: number
  n_trials: number
  n_sessions: number
  /** `true` si su fila ya viene en `entries` (se resalta en vez de repetirse). */
  in_top: boolean
}

export interface RankingDto {
  scenario_type: string
  period_type: RankingPeriod
  period_key: string
  entries: RankingEntry[]
  computed_at: string
  /** Jugadores rankeados en el bucket — denominador de "de N". */
  total: number
  me: RankingMe | null
}

export const know = {
  responses: () => api.get<{ items: ResponseSummary[] }>('/know/responses'),
  detail: (code: string) =>
    api.get<{
      definition: QuestionnaireDefinition
      response: ResponseDetail | null
      responses: ResponseDetail[]
    }>(`/know/${encodeURIComponent(code)}`),
  submit: (
    code: string,
    answers: Record<string | number, AnswerValue>,
    timepoint?: string,
  ) =>
    api.post<ResponseDetail>(`/know/${encodeURIComponent(code)}/submit`, {
      answers,
      ...(timepoint ? { timepoint } : {}),
    }),
  /** PATCH para cuestionarios `editable=true` (hoy: DEMOGRAPHIC). */
  updateResponse: (
    code: string,
    answers: Record<string | number, AnswerValue>,
  ) =>
    api.patch<ResponseDetail>(
      `/know/${encodeURIComponent(code)}/submit`,
      { answers },
    ),
  profile: () => api.get<ProfileDto>('/know/me/profile'),
  profileOptions: () => api.get<ProfileOptions>('/know/me/profile/options'),
  performance: () => api.get<PerformanceDto>('/know/me/performance'),
  /** Exige sesión (cookie, siempre adjunta — ver `api/client.ts`) y viene
   *  scopeado al club del que consulta; el bloque `me` es su propio puesto.
   *  `all` es el bucket global de la experiencia (todos los escenarios). */
  ranking: (
    period: RankingPeriod = 'all_time',
    limit = 10,
    scenarioType = 'all',
  ) =>
    api.get<RankingDto>(
      `/ranking/${encodeURIComponent(scenarioType)}?${new URLSearchParams({
        period_type: period,
        limit: String(limit),
      }).toString()}`,
    ),
  updateProfile: (patch: ProfilePatch) =>
    api.patch<ProfileDto>('/know/me/profile', patch),
  /** Resolver público — no requiere auth. Lo llama la ruta /invite/:token. */
  resolveInvitation: (token: string) =>
    api.get<InvitationInfo>(`/invitations/${encodeURIComponent(token)}`),
  /** Self-consent in-app (gate de `/consent`, ver `pages/ConsentGate.tsx`). Body
   *  vacío — idempotente: si ya hay un Consent vigente, el backend lo
   *  devuelve tal cual en vez de duplicar. */
  recordMyConsent: () => api.post<ConsentDto>('/know/me/consent', {}),
}


export function timepointLabel(value: string | null | undefined): string {
  return ({ 'gf25-baseline': 'Evaluación inicial', 'gf25-post': 'Evaluación posterior', 'gf25-followup': 'Seguimiento a 3 meses' } as Record<string, string>)[value ?? ''] ?? value ?? '—'
}
